import { useCallback, useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { staffApi, errMsg, fmtDate } from "./api";
import { Status } from "./Shell";

const Row = ({ rec, list, onDeleted }) => {
  const [confirm, setConfirm] = useState(false);
  const [status, setStatus] = useState({});
  const remove = async () => {
    setStatus({ state: "loading" });
    try {
      await staffApi.delete(`/admin/records/${list}/${rec.reference}`);
      onDeleted();
    } catch (err) {
      setStatus({ state: "error", msg: await errMsg(err) });
    }
  };
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 p-4 text-sm" data-testid={`staff-record-${rec.reference}`}>
      <div>
        <p className="font-semibold text-navy">{rec.name || "(no name)"} <span className="font-normal text-slate">· {rec.reference}</span></p>
        <p className="text-slate">{rec.email} · {fmtDate(rec.created_at)}</p>
        <Status status={status} />
      </div>
      {confirm ? (
        <span className="flex gap-2">
          <button type="button" onClick={remove} className="btn btn-red !px-4 !py-2" data-testid={`staff-record-confirm-delete-${rec.reference}`}>Delete permanently</button>
          <button type="button" onClick={() => setConfirm(false)} className="btn btn-ghost !px-4 !py-2" data-testid={`staff-record-cancel-delete-${rec.reference}`}>Cancel</button>
        </span>
      ) : (
        <button type="button" onClick={() => setConfirm(true)} className="btn btn-ghost !px-4 !py-2" data-testid={`staff-record-delete-${rec.reference}`}>
          <Trash2 size={15} /> Delete
        </button>
      )}
    </li>
  );
};

const DeleteAll = ({ list, title, onDone }) => {
  const [text, setText] = useState("");
  const [status, setStatus] = useState({});
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await staffApi.post(`/admin/records/${list}/delete-all`, { confirm: text });
      setText("");
      setStatus({ state: "success", msg: `${data?.deleted ?? 0} records deleted.` });
      onDone();
    } catch (err) {
      setStatus({ state: "error", msg: await errMsg(err) });
    }
  };
  return (
    <form onSubmit={submit} className="mt-8 space-y-3 border border-fire/40 bg-white p-5" data-testid="staff-delete-all-form">
      <p className="font-semibold text-fire">Delete the whole "{title}" list</p>
      <p className="text-sm text-slate">This can't be undone. Download a copy first if you need one. Type DELETE to confirm.</p>
      <div className="flex flex-wrap gap-3">
        <input className="field-input max-w-[200px]" value={text} onChange={(e) => setText(e.target.value)} aria-label="Type DELETE to confirm" data-testid="staff-delete-all-input" />
        <button type="submit" disabled={text !== "DELETE"} className="btn btn-red disabled:opacity-50" data-testid="staff-delete-all-button">Delete All</button>
      </div>
      <Status status={status} testId="staff-delete-all-status" />
    </form>
  );
};

export const RecordsPanel = ({ lists }) => {
  const [list, setList] = useState(lists?.[0]?.key ?? "");
  const [recs, setRecs] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    if (!list) return;
    setRecs(null);
    staffApi.get(`/admin/records/${list}`)
      .then((r) => setRecs(Array.isArray(r.data) ? r.data : []))
      .catch(async (e) => setError(await errMsg(e)));
  }, [list]);
  useEffect(() => { load(); }, [load]);
  const title = lists?.find((l) => l.key === list)?.title ?? "";
  return (
    <div data-testid="staff-records-panel">
      <p className="mb-6 max-w-2xl text-sm text-slate">Records are kept until an Administrator deletes them. Deleting removes the record from the website database. It does not delete copies already emailed or downloaded.</p>
      <select className="field-input max-w-sm" value={list} onChange={(e) => setList(e.target.value)} aria-label="Choose a list" data-testid="staff-records-list-select">
        {(lists ?? []).map((l) => <option key={l.key} value={l.key}>{l.title}</option>)}
      </select>
      {error && <p className="mt-4 text-sm text-fire" role="alert">{error}</p>}
      {!recs ? <Loader2 className="mt-6 animate-spin text-navy" /> : (
        <>
          <p className="mt-6 text-sm text-slate" data-testid="staff-records-count">{recs.length} record{recs.length === 1 ? "" : "s"}</p>
          <ul className="mt-3 divide-y divide-mist border border-mist bg-white">
            {recs.map((r) => <Row key={r.reference} rec={r} list={list} onDeleted={load} />)}
          </ul>
          {recs.length > 0 && <DeleteAll list={list} title={title} onDone={load} />}
        </>
      )}
    </div>
  );
};
